import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Decorative icon pinned inside the leading edge. */
  startIcon?: ReactNode;
  /** Icon or control pinned inside the trailing edge — a clear button, a unit. */
  endIcon?: ReactNode;
  /** Applied to the wrapper when an icon is present, not to the field itself. */
  wrapperClassName?: string;
}

/*
 * A field rests inset — tinted background, tinted border, no elevation — and comes
 * forward on focus: the surface lifts to the card white, the border takes the brand
 * green, and a soft tint ring spreads behind it. Three properties moving together is
 * what makes the state change legible without a heavy outline.
 */
const fieldStyles = [
  'flex h-12 w-full rounded-md border border-input bg-field px-4 py-2 text-base',
  'transition-[border-color,box-shadow,background-color] duration-fast ease-out',
  'placeholder:text-muted-subtle',
  'hover:border-muted-subtle/60',
  // The ring is the brand indigo at 12%, not the pale accent tint: against the white
  // the field lifts to on focus, a tint ring is nearly invisible, and the ring is the
  // part of the state change that reads from across the form.
  'focus-visible:border-primary focus-visible:bg-card focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/[0.12] focus-visible:ring-offset-0',
  'disabled:cursor-not-allowed disabled:bg-muted/60 disabled:opacity-60',
  'aria-[invalid=true]:border-destructive aria-[invalid=true]:ring-destructive/20',
  // The native search affordance is inconsistent across browsers and ignores the
  // design tokens; the clear action is provided explicitly where it is wanted.
  '[&::-webkit-search-cancel-button]:appearance-none',
  '[&::-webkit-calendar-picker-indicator]:opacity-60',
];

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, startIcon, endIcon, wrapperClassName, ...props }, ref) => {
    const field = (
      <input
        type={type}
        ref={ref}
        className={cn(
          fieldStyles,
          startIcon ? 'pl-11' : null,
          endIcon ? 'pr-11' : null,
          className,
        )}
        {...props}
      />
    );

    if (!startIcon && !endIcon) return field;

    return (
      <div className={cn('relative', wrapperClassName)}>
        {startIcon ? (
          <span
            className="pointer-events-none absolute left-4 top-1/2 flex -translate-y-1/2 items-center text-muted-foreground [&_svg]:size-4"
            aria-hidden="true"
          >
            {startIcon}
          </span>
        ) : null}

        {field}

        {endIcon ? (
          <span className="absolute right-3.5 top-1/2 flex -translate-y-1/2 items-center text-muted-foreground [&_svg]:size-4">
            {endIcon}
          </span>
        ) : null}
      </div>
    );
  },
);
Input.displayName = 'Input';

export type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement>;

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, ...props }, ref) => (
    <textarea
      ref={ref}
      className={cn(fieldStyles, 'h-auto min-h-[5rem] resize-y py-2 leading-relaxed', className)}
      {...props}
    />
  ),
);
Textarea.displayName = 'Textarea';
